"use client";

import React, { useEffect } from "react";
import { useRouter } from "next/navigation";

import { selectLoginStatus, useAppSelector } from "@repo/global-store";
import { APP_PROMPTS } from "@repo/shared-utils/pwa.constants";
import { getSSOConfigsFromUrl, ssoRedirect } from "@repo/sso/redirect-utils";
import { isNextPage } from "@repo/shared-utils/common-utils";
import { dataLayer } from "@repo/analytics/data-layer-utils";

type ClickHandlerProps = {
  shouldStopPropagation?: (href: string) => boolean;
  /** When true, skips dataLayer / CleverTap for this click (navigation still runs). */
  shouldSkipAnalytics?: (pathname: string) => boolean;
};

/**
 * ClickHandler
 * @returns
 */
export default function ClickHandler({
  shouldStopPropagation,
  shouldSkipAnalytics,
}: ClickHandlerProps = {}) {
  // Get auth status from store if not provided as prop
  const loginStatus = useAppSelector(selectLoginStatus);
  const router = useRouter();

  /**
   * getNearestAnchorElement
   * @param element
   * @returns
   */
  const getNearestAnchorElement = (element: HTMLElement): HTMLAnchorElement | null => {
    if (element.tagName === "A") {
      return element as HTMLAnchorElement;
    }
    const anchorElement = element.closest("a");
    if (anchorElement) {
      return anchorElement;
    }
    return null;
  };

  /**
   * handleAnchorClick
   * @param e
   * @param anchorElement
   */
  const handleHrefClick = async (e: MouseEvent, anchorElement: HTMLAnchorElement) => {
    const href = anchorElement.href.toLowerCase();
    e.preventDefault();
    // Check for Bajaj Super App redirection
    if (href.includes(APP_PROMPTS.BAJAJ_SUPER_APP)) {
      e.preventDefault();
      const windowWithTarsier = window as Window & {
        tarsier?: {
          invokePrompt: (prompt: string, force?: boolean) => unknown;
        };
      };
      windowWithTarsier.tarsier?.invokePrompt(
        `${APP_PROMPTS.IN_APP_REDIRECTION}${anchorElement.href}`,
        true
      );
      return;
    }

    const mapping = getSSOConfigsFromUrl(anchorElement.href);
    const relativeHref = anchorElement.getAttribute("href") ?? anchorElement.href;
    const openInSameTab = shouldStopPropagation?.(anchorElement.href) ?? false;
    if (mapping?.ssoModule) {
      ssoRedirect(
        {
          href: anchorElement.href,
          target: openInSameTab ? "_self" : anchorElement.target,
          checkCart: true,
          showResumePopup: true,
        },
        loginStatus,
        mapping
      );
      return;
    } else {
      try {
        const dlValue = JSON.parse(anchorElement.getAttribute("data-dl") || "{}");
        const ctValue = JSON.parse(anchorElement.getAttribute("data-ct") || "{}");
        const skipAnalytics = shouldSkipAnalytics?.(window.location.pathname) ?? false;
        if (!skipAnalytics) {
          dataLayer(loginStatus, dlValue, ctValue);
        }
      } catch (err) {
        console.error("Error parsing ct/dl props:", err);
      }
      if (!isNextPage(relativeHref) || anchorElement.target === "_blank") {
        window.open(anchorElement.href, anchorElement.target || "_self");
      } else {
        router.push(anchorElement.href);
      }
    }
  };

  /**
   * addClickListener
   */
  const addClickListener = async () => {
    document?.addEventListener(
      "click",
      (e) => {
        const target = e.target as HTMLElement;
        const anchorElement = getNearestAnchorElement(target);
        if (!anchorElement) return;

        // Links that handle redirection programmatically (e.g. CLP listing tiles)
        if (anchorElement.hasAttribute("data-clp-redirection")) return;

        const href = anchorElement.href;
        if (!href) return;

        if (shouldStopPropagation?.(href)) {
          e.stopPropagation();
        }

        // Avoid blocking main thread; defer heavy logic
        queueMicrotask(() => handleHrefClick(e, anchorElement));
      },
      { capture: true }
    );
  };

  // Track if the effect has already run
  const hasRunRef = React.useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    if (loginStatus !== "pending") {
      addClickListener();
      hasRunRef.current = true;
    }
  }, [loginStatus]);

  // This component doesn't render anything
  return null;
}
